import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  CreatePosterFromTemplateRequest,
  CreatePosterRequest,
  MatchRequest,
  Poster,
  TournamentRequest,
} from './types';

const keys = {
  providers: ['providers'] as const,
  posterTemplates: ['poster-templates'] as const,
  tournaments: (season?: string) => ['tournaments', season ?? 'all'] as const,
  tournament: (id: string) => ['tournament', id] as const,
  matches: (tournamentId: string) => ['matches', tournamentId] as const,
  posters: (tournamentId: string) => ['posters', tournamentId] as const,
};

export function useProviders() {
  return useQuery({ queryKey: keys.providers, queryFn: api.providers, staleTime: 5 * 60 * 1000 });
}

export function usePosterTemplates() {
  return useQuery({
    queryKey: keys.posterTemplates,
    queryFn: api.posterTemplates,
    staleTime: 30 * 60 * 1000,
  });
}

/** Creating a poster can also create the competition it belongs to, so both lists are refreshed. */
export function useCreatePosterFromTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePosterFromTemplateRequest) => api.createPosterFromTemplate(body),
    onSuccess: (created) => {
      void client.invalidateQueries({ queryKey: ['tournaments'] });
      void client.invalidateQueries({ queryKey: ['posters', created.tournamentId] });
    },
  });
}

export function useTournaments(season?: string) {
  return useQuery({ queryKey: keys.tournaments(season), queryFn: () => api.listTournaments(season) });
}

export function useTournament(id: string) {
  return useQuery({ queryKey: keys.tournament(id), queryFn: () => api.getTournament(id), enabled: Boolean(id) });
}

export function useCreateTournament() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TournamentRequest) => api.createTournament(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tournaments'] }),
  });
}

export function useUpdateTournament(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TournamentRequest) => api.updateTournament(id, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['tournaments'] });
      void client.invalidateQueries({ queryKey: keys.tournament(id) });
    },
  });
}

export function useDeleteTournament() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTournament(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tournaments'] }),
  });
}

export function useUploadBackground(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadBackground(tournamentId, file),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.tournament(tournamentId) }),
  });
}

export function useMatches(tournamentId: string) {
  return useQuery({
    queryKey: keys.matches(tournamentId),
    queryFn: () => api.listMatches(tournamentId),
    enabled: Boolean(tournamentId),
  });
}

export function useSaveMatch(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, body }: { matchId?: string; body: MatchRequest }) =>
      matchId ? api.updateMatch(tournamentId, matchId, body) : api.createMatch(tournamentId, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.matches(tournamentId) }),
  });
}

export function useDeleteMatch(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => api.deleteMatch(tournamentId, matchId),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.matches(tournamentId) }),
  });
}

/**
 * Poster generation is asynchronous on the server, so the list polls itself while anything is
 * still pending or generating and goes quiet again as soon as everything has settled.
 */
export function usePosters(tournamentId: string) {
  return useQuery({
    queryKey: keys.posters(tournamentId),
    queryFn: () => api.listPosters(tournamentId),
    enabled: Boolean(tournamentId),
    refetchInterval: (query) => {
      const posters = query.state.data as Poster[] | undefined;
      const working = posters?.some((p) => p.status === 'Pending' || p.status === 'Generating');
      return working ? 4000 : false;
    },
  });
}

export function useCreatePoster(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePosterRequest) => api.createPoster(tournamentId, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.posters(tournamentId) }),
  });
}

export function useRegeneratePoster(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (posterId: string) => api.regeneratePoster(tournamentId, posterId),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.posters(tournamentId) }),
  });
}

export function useSelectPosterVersion(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ posterId, versionId }: { posterId: string; versionId: string }) =>
      api.selectPosterVersion(tournamentId, posterId, versionId),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.posters(tournamentId) }),
  });
}

export function useDeletePoster(tournamentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (posterId: string) => api.deletePoster(tournamentId, posterId),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.posters(tournamentId) }),
  });
}
